/**
 * Wallet capability detection for Deposit/Land Protection.
 *
 * `useWalletCapabilities()` runs the static detector in
 * [src/lib/wallet-capabilities.ts](../lib/wallet-capabilities.ts) against
 * the currently-connected wallet and returns `supports7702` + a
 * marketing-friendly wallet name. Land Protection is MetaMask-only today
 * (the only wallet whose ERC-7715 grant our DelegationManager can redeem),
 * so the detector returns `signingPath: '7715-grant'` for MetaMask and
 * `supports7702: false` for every other wallet; the UI uses that to render
 * the MetaMask-only state. Polled via React Query so capability changes
 * (wallet switch, account switch) refresh automatically.
 *
 * The former `useDelegationSigner` (7710-direct EIP-712 signing + the
 * EIP-7702 self-upgrade helpers) was removed when the 7710 enable rail was
 * retired: external wallets can't install a DM-compatible delegator
 * (Rabby blocks the dapp authorization, Phantom strips it), so MetaMask's
 * 7715 grant is the only supported enable path. Redemption of any legacy
 * 7710 rows is unaffected — it lives entirely server/SDK-side.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type Address } from 'viem';
import { useWallets } from '@privy-io/react-auth';
import { useWallet } from './use-wallet';
import {
  COMPATIBLE_WALLETS,
  detectEip7702Support,
  type WalletCapabilityResult,
} from '@/lib/wallet-capabilities';

// ─── Capability detection hook ────────────────────────────────────

export function useWalletCapabilities(chainId: number): {
  result: WalletCapabilityResult | null;
  isLoading: boolean;
  /** Stable list for "Compatible: …" tooltips. */
  compatibleWallets: typeof COMPATIBLE_WALLETS;
} {
  const { address, isConnected } = useWallet();
  const { wallets } = useWallets();

  // Find the active wallet's metadata. Privy returns a richer shape
  // than wagmi (label, walletClientType, etc.).
  const active = useMemo(() => {
    if (!address) return null;
    return wallets.find((w) => w.address?.toLowerCase() === address.toLowerCase()) ?? null;
  }, [wallets, address]);

  const query = useQuery({
    queryKey: ['wallet-capabilities', address, chainId, active?.connectorType, active?.walletClientType],
    enabled: isConnected && !!address,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: async () => {
      if (!active) return null;
      // Privy's `getEthereumProvider()` returns the EIP-1193 provider
      // for the active wallet. Cast to the shape our detector wants.
      let provider: Awaited<ReturnType<typeof active.getEthereumProvider>> | null = null;
      try {
        provider = await active.getEthereumProvider();
      } catch {
        provider = null;
      }
      const result = await detectEip7702Support({
        connectorId: active.connectorType ?? null,
        walletLabel: active.walletClientType ?? undefined,
        provider: provider as Parameters<typeof detectEip7702Support>[0]['provider'],
        account: address as Address,
        chainId,
      });
      return result;
    },
  });

  return {
    result: query.data ?? null,
    isLoading: query.isLoading,
    compatibleWallets: COMPATIBLE_WALLETS,
  };
}
